import { getSafeUrl } from "../../utils/helpers.js";
import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Form,
  Input,
  Label,
} from "reactstrap";
import Header from "components/Headers/Header";
import { get, post, put, del } from "utils/api";
import { encryptDecryptData } from "utils/helperFunction";
import { toast } from "react-toastify";
import axios from "axios";

const TrustedPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    company: "",
    description: "",
    vendorImage: null, // Holds actual File object or existing string URL
    link: ""
  });

  const [vendorImagePreview, setVendorImagePreview] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const getToken = () => {
    let storedData;
    try {
      storedData = encryptDecryptData.decrypt(localStorage.getItem("user"));
    } catch (e) {
      console.error("Error decrypting user data", e);
    }
    return storedData?.token || "";
  };

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const res = await get("/api/trusted-partner", {});
      let list = [];
      if (res?.data && Array.isArray(res.data)) {
        list = res.data;
      } else if (Array.isArray(res)) {
        list = res;
      } else if (res?.partners && Array.isArray(res.partners)) {
        list = res.partners;
      }
      setPartners(list);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load partners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setFormData({
        id: "",
        name: "",
        company: "",
        description: "",
        vendorImage: null,
        link: ""
      });
      setVendorImagePreview("");
      setIsEdit(false);
    }
  };

  const openAddModal = () => {
    setFormData({
      id: "",
      name: "",
      company: "",
      description: "",
      vendorImage: null,
      link: ""
    });
    setVendorImagePreview("");
    setIsEdit(false);
    setModalOpen(true);
  };

  const openEditModal = (partner) => {
    setFormData({
      id: partner._id || partner.id || "",
      name: partner.name || "",
      company: partner.company || "",
      description: partner.description || "",
      vendorImage: partner.vendorImage || null,
      link: partner.link || ""
    });
    setVendorImagePreview(getSafeUrl(partner.vendorImage) || "");
    setIsEdit(true);
    setModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData(prev => ({ ...prev, vendorImage: file }));
    setVendorImagePreview(URL.createObjectURL(file));
  };


  const submitTrustedPartner = async (isEditMode, payloadData) => {
    const BASE_URL = process.env.REACT_APP_BASE_URL || "";
    const submitData = new FormData();
    submitData.append("name", payloadData.name);
    submitData.append("company", payloadData.company);
    submitData.append("description", payloadData.description);
    submitData.append("link", payloadData.link);

    if (payloadData.vendorImage instanceof File) {
      submitData.append("vendorImage", payloadData.vendorImage);
    }

    const config = {
      headers: {
        'Authorization': `${getToken()}`,
        'Content-Type': 'multipart/form-data'
      }
    };

    if (isEditMode) {
      return await axios.put(`${BASE_URL}/api/trusted-partner/${payloadData.id}`, submitData, config);
    } else {
      return await axios.post(`${BASE_URL}/api/trusted-partner`, submitData, config);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);

    try {
      if (isEdit) {
        await submitTrustedPartner(true, formData);
        toast.success("Partner updated successfully");
      } else {
        await submitTrustedPartner(false, formData);
        toast.success("Partner created successfully");
      }
      toggleModal();
      fetchPartners();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to save partner");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this partner?")) return;
    try {
      await del(`/api/trusted-partner/${id}`, { 'Authorization': getToken() });
      toast.success("Partner deleted successfully");
      fetchPartners();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete partner");
    }
  };

  return (
    <>
      <style>{`
        .modal-backdrop-blur {
          opacity: 1 !important;
          background-color: rgba(0, 0, 0, 0.4) !important;
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
        }
      `}</style>
      <Header />
      <Container className="mt-4" fluid>
        <Card className="shadow">
          <CardHeader className="border-0 d-flex justify-content-between align-items-center">
            <h3 className="mb-0">Trusted Partners (Vendors)</h3>
            <Button style={{ background: "#0474ba", color: "#ffff" }} onClick={openAddModal}>Add Partner</Button>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : (
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Partner</th>
                    <th scope="col">Company</th>
                    <th scope="col">Link</th>
                    <th scope="col" className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners && partners.length > 0 ? (
                    partners.map((partner) => {
                      const id = partner._id || partner.id;
                      return (
                        <tr key={id}>
                          <th scope="row">
                            <div className="d-flex align-items-center">
                              {partner.vendorImage ? (
                                <img
                                  src={getSafeUrl(partner.vendorImage)}
                                  alt={partner.name}
                                  style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover", marginRight: "10px" }}
                                />
                              ) : (
                                <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#ddd", marginRight: "10px" }} />
                              )}
                              <span>{partner.name}</span>
                            </div>
                          </th>
                          <td>{partner.company}</td>
                          <td>
                            {partner.link ? (
                              <a href={partner.link} target="_blank" rel="noreferrer">Visible Link</a>
                            ) : "N/A"}
                          </td>
                          <td className="text-right">
                            <Button
                              color="info"
                              size="sm"
                              className="mr-2"
                              onClick={() => openEditModal(partner)}
                            >
                              Edit
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">No trusted partners found.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </Container>

      {/* Add / Edit Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal} backdropClassName="modal-backdrop-blur">
        <ModalHeader toggle={toggleModal}>
          {isEdit ? "Edit Trusted Partner" : "Add Trusted Partner"}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>Name *</Label>
              <Input
                required
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Partner Name"
              />
            </FormGroup>
            <FormGroup>
              <Label>Company *</Label>
              <Input
                required
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Company Name"
              />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Description"
              />
            </FormGroup>
            <FormGroup>
              <Label>Partner Link (URL)</Label>
              <Input
                type="url"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </FormGroup>
            <FormGroup>
              <Label>Vendor Image</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
              />
              {vendorImagePreview && (
                <div className="mt-2">
                  <img
                    src={vendorImagePreview}
                    alt="Preview"
                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }}
                  />
                </div>
              )}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal} type="button">
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isUploading}>
              {isEdit ? "Update" : "Save"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default TrustedPartners;
